import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Edit2, Shield, Camera, Bell, Eye, MapPin, Heart, Sparkles, ChevronRight, LogOut, HelpCircle, FileText } from 'lucide-react';
import { User } from '../types';
import { SettingsModal } from './SettingsModal';
import { EditProfileModal } from './EditProfileModal';
import { SafetyModal } from './SafetyModal';
interface ProfileViewProps {
  user: User;
}
export function ProfileView({
  user
}: ProfileViewProps) {
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showSafety, setShowSafety] = useState(false);
  const menuSections = [{
    title: 'Account',
    items: [{
      icon: Bell,
      label: 'Notifications',
      value: 'On',
      onClick: () => setShowSettings(true)
    }, {
      icon: Eye,
      label: 'Who can see me',
      value: 'Everyone',
      onClick: () => setShowSafety(true)
    }, {
      icon: MapPin,
      label: 'Location',
      value: 'New York',
      onClick: () => setShowSettings(true)
    }]
  }, {
    title: 'Discovery',
    items: [{
      icon: Heart,
      label: 'Looking for',
      value: user.lookingFor || 'Relationship',
      onClick: () => setShowEditProfile(true)
    }, {
      icon: Settings,
      label: 'Distance',
      value: '25 miles',
      onClick: () => setShowSettings(true)
    }, {
      icon: Settings,
      label: 'Age range',
      value: '21 - 35',
      onClick: () => setShowSettings(true)
    }]
  }, {
    title: 'Support',
    items: [{
      icon: HelpCircle,
      label: 'Help Center',
      value: ''
    }, {
      icon: FileText,
      label: 'Terms & Privacy',
      value: ''
    }, {
      icon: LogOut,
      label: 'Log Out',
      value: '',
      danger: true
    }]
  }];
  return <div className="h-full overflow-y-auto bg-zinc-50 pb-24" data-id="element-358">
      {/* Header with Photo Gallery */}
      <div className="relative h-80" data-id="element-359">
        <img src={user.photos[activePhotoIndex]} alt={user.name} className="w-full h-full object-cover transition-opacity duration-300" data-id="element-360" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-361" />

        {/* Photo Indicators */}
        <div className="absolute top-4 left-4 right-4 flex gap-1" data-id="element-362">
          {user.photos.map((_, idx) => <button key={idx} onClick={() => setActivePhotoIndex(idx)} className={`h-1 flex-1 rounded-full transition-colors ${idx === activePhotoIndex ? 'bg-white' : 'bg-white/40'}`} data-id="element-363" />)}
        </div>

        {/* Edit Photo Button */}
        <button onClick={() => setShowEditProfile(true)} className="absolute top-4 right-4 p-2 bg-black/30 backdrop-blur-md rounded-full text-white hover:bg-black/50 transition-colors" data-id="element-364">
          <Camera size={20} data-id="element-365" />
        </button>

        {/* User Info */}
        <div className="absolute bottom-4 left-4 right-4 text-white" data-id="element-366">
          <div className="flex items-center gap-2 mb-1" data-id="element-367">
            <h1 className="text-2xl font-bold" data-id="element-368">
              {user.name}, {user.age}
            </h1>
            {user.verified && <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-369">
                <Sparkles size={14} className="text-white" data-id="element-370" />
              </div>}
          </div>
          <p className="text-white/80 text-sm" data-id="element-371">{user.job}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex justify-center gap-4 -mt-6 relative z-10 px-4 mb-6" data-id="element-372">
        <motion.button whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={() => setShowSettings(true)} className="flex flex-col items-center" data-id="element-373">
          <div className="w-14 h-14 bg-white rounded-full shadow-lg flex items-center justify-center text-zinc-400 hover:text-rose-500 transition-colors mb-1" data-id="element-374">
            <Settings size={22} data-id="element-375" />
          </div>
          <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide" data-id="element-376">
            Settings
          </span>
        </motion.button>

        <motion.button whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={() => setShowEditProfile(true)} className="flex flex-col items-center -mt-3" data-id="element-377">
          <div className="w-18 h-18 bg-gradient-to-br from-orange-500 to-rose-500 rounded-full shadow-lg shadow-rose-500/30 flex items-center justify-center text-white mb-1 p-5" data-id="element-378">
            <Edit2 size={28} data-id="element-379" />
          </div>
          <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide" data-id="element-380">
            Edit Profile
          </span>
        </motion.button>

        <motion.button whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={() => setShowSafety(true)} className="flex flex-col items-center" data-id="element-381">
          <div className="w-14 h-14 bg-white rounded-full shadow-lg flex items-center justify-center text-zinc-400 hover:text-blue-500 transition-colors mb-1" data-id="element-382">
            <Shield size={22} data-id="element-383" />
          </div>
          <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide" data-id="element-384">
            Safety
          </span>
        </motion.button>
      </div>

      {/* Premium Banner */}
      <div className="mx-4 mb-6" data-id="element-385">
        <motion.div whileHover={{
        scale: 1.02
      }} className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl p-4 shadow-lg cursor-pointer" data-id="element-386">
          <div className="flex items-center gap-4" data-id="element-387">
            <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center" data-id="element-388">
              <Sparkles size={24} className="text-white" data-id="element-389" />
            </div>
            <div className="flex-1" data-id="element-390">
              <h3 className="font-bold text-white" data-id="element-391">Spark Premium</h3>
              <p className="text-white/80 text-sm" data-id="element-392">See who likes you & more!</p>
            </div>
            <button className="px-4 py-2 bg-white text-orange-500 text-sm font-bold rounded-full shadow-md" data-id="element-393">
              Upgrade
            </button>
          </div>
        </motion.div>
      </div>

      {/* Profile Completion */}
      <div className="mx-4 mb-6" data-id="element-394">
        <div className="bg-white rounded-2xl p-4 shadow-sm" data-id="element-395">
          <div className="flex items-center justify-between mb-3" data-id="element-396">
            <span className="font-semibold text-zinc-900" data-id="element-397">
              Profile Completion
            </span>
            <span className="text-rose-500 font-bold" data-id="element-398">75%</span>
          </div>
          <div className="h-2 bg-zinc-100 rounded-full overflow-hidden" data-id="element-399">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: '75%'
          }} transition={{
            duration: 1,
            delay: 0.5
          }} className="h-full bg-gradient-to-r from-orange-500 to-rose-500 rounded-full" data-id="element-400" />
          </div>
          <p className="text-xs text-zinc-500 mt-2" data-id="element-401">
            Add more photos to get 3x more matches!
          </p>
        </div>
      </div>

      {/* Menu Sections */}
      <div className="px-4 space-y-4" data-id="element-402">
        {menuSections.map((section, sectionIndex) => <div key={sectionIndex} className="bg-white rounded-2xl shadow-sm overflow-hidden" data-id="element-403">
            <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider px-4 pt-4 pb-2" data-id="element-404">
              {section.title}
            </h3>
            {section.items.map((item, itemIndex) => <button key={itemIndex} onClick={item.onClick} className={`w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors ${itemIndex !== section.items.length - 1 ? 'border-b border-zinc-50' : ''}`} data-id="element-405">
                <div className="flex items-center gap-3" data-id="element-406">
                  <item.icon size={20} className={item.danger ? 'text-red-500' : 'text-zinc-400'} data-id="element-407" />
                  <span className={`font-medium ${item.danger ? 'text-red-500' : 'text-zinc-900'}`} data-id="element-408">
                    {item.label}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-409">
                  {item.value && <span className="text-sm text-zinc-500" data-id="element-410">{item.value}</span>}
                  <ChevronRight size={18} className="text-zinc-300" data-id="element-411" />
                </div>
              </button>)}
          </div>)}
      </div>

      {/* App Version */}
      <p className="text-center text-xs text-zinc-400 mt-8 mb-4" data-id="element-412">
        Spark v1.0.0
      </p>

      {/* Modals */}
      <AnimatePresence data-id="element-413">
        {showSettings && <SettingsModal onClose={() => setShowSettings(false)} data-id="element-414" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-415">
        {showEditProfile && <EditProfileModal user={user} onClose={() => setShowEditProfile(false)} data-id="element-416" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-417">
        {showSafety && <SafetyModal onClose={() => setShowSafety(false)} data-id="element-418" />}
      </AnimatePresence>
    </div>;
}